import { HttpResponse, ServiceClient } from '@johnlewispartnership/wtr-website-api-client';
import config from 'config';
import { logApiError } from 'logger';
import {
  OrderOptions,
  PlaceOrderError,
  PlaceOrderResponse,
  PlaceOrderResult,
} from 'services/orderOrchestration/types';
import getApiBaseUrl from 'utils/getApiBaseUrl';

const orderOrchestrationApiUrl = getApiBaseUrl(config.services.orderOrchestration.path);

const serviceName = 'orderOrchestration';

class OrderOrchestrationServiceClient extends ServiceClient {
  async placeOrder({
    authHeader,
    customerOrderId,
    orderOptions,
  }: {
    authHeader: string;
    customerOrderId: string;
    orderOptions: OrderOptions;
  }): Promise<PlaceOrderResult> {
    const path = `/v1/orders/${customerOrderId}/place`;
    const response: HttpResponse<PlaceOrderResponse> = await this.httpClient
      .patch<OrderOptions & { event: string }, PlaceOrderResponse>(
        path,
        {
          event: 'PLACE',
          ...orderOptions,
        },
        {
          headers: {
            Authorization: authHeader,
          },
          timeout: 45000,
        },
      )
      .catch(e => ({ error: e } as HttpResponse<PlaceOrderResponse>));

    const responseData = response.data;

    if (!responseData || response.error) {
      logApiError({
        payload: {
          service: serviceName,
          url: path,
          message: `Error placing order: ${response.error?.message || 'Unknown error'}`,
          duration: response.duration,
          /**
           * This field has a different format according to our logging standards (e.g.: "200 (OK)" ).
           * It's a string value, which means we can't do queries like "httpStatus > 200 AND httpStatus < 300".
           * We might want to get the standards changed.
           * See logging standards: https://www.jlpit.com/wiki/pages/viewpage.action?spaceKey=WD&title=Waitrose+E-commerce+logging+standards
           */
          httpStatus: `${response.status}`,
          // This field is not documented in the logging standards, but it's a number, so we can use relational operators in our queries.
          httpStatusCode: response.status,
        },
      });
    }
    return {
      ...(response.error && {
        error: (response.data as PlaceOrderError) || {
          code: response.error.timeout ? 'TIMEOUT' : 'UNKONWN_ERROR',
        },
      }),
    };
  }
}

const orderOrchestrationServiceClient = new OrderOrchestrationServiceClient(
  orderOrchestrationApiUrl,
);

export default orderOrchestrationServiceClient;
